import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  BarChart3, 
  Users, 
  DollarSign, 
  Package, 
  TrendingUp, 
  TrendingDown,
  ShoppingCart,
  AlertTriangle,
  Clock,
  Calendar,
  Eye,
  Plus,
  ArrowRight,
  Star,
  Target,
  Activity,
  CreditCard,
  Smartphone
} from 'lucide-react';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { dataHelpers } from '../../lib/supabase';
import toast from 'react-hot-toast';

const DashboardPage = () => {
  const { company, person } = useAuth();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    metrics: {
      todayTotal: 0,
      monthTotal: 0,
      studentsCount: 0,
      productsCount: 0,
      todayTransactions: 0,
      averageTicket: 0,
      topProduct: '',
      lowStockCount: 0
    },
    todayStats: {
      sales: [],
      hourlyComparison: { current: 0, yesterday: 0, change: 0 }
    },
    recentSales: [],
    lowStockProducts: []
  });

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (company?.id) {
      loadDashboardData();
    }
  }, [company?.id]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      // Carregar dados em paralelo
      const [
        metrics,
        additionalMetrics,
        todayStats,
        recentSales,
        lowStockProducts
      ] = await Promise.all([
        dataHelpers.getDashboardMetrics(company.id),
        dataHelpers.getAdditionalMetrics(company.id),
        dataHelpers.getTodayStats(company.id),
        dataHelpers.getRecentSales(company.id, 4),
        dataHelpers.getLowStockProducts(company.id, 4)
      ]);
      setDashboardData({
        metrics: {
          ...metrics,
          ...additionalMetrics,
          lowStockCount: lowStockProducts.length
        },
        todayStats,
        recentSales,
        lowStockProducts
      });
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toast.error('Erro ao carregar dados do dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Extrair métricas para facilitar o acesso
  const { metrics, todayStats, recentSales, lowStockProducts } = dashboardData;

  const quickActions = [
    {
      title: 'Mensalidades',
      description: 'Gerenciar planos e assinaturas',
      icon: CreditCard,
      href: '/dashboard/monthly-payments',
      color: 'bg-blue-500',
      urgent: false
    },
    {
      title: 'Abrir PDV',
      description: 'Iniciar ponto de venda',
      icon: CreditCard,
      href: '/dashboard/pdv',
      color: 'bg-green-500',
      urgent: false
    },
    {
      title: 'Cadastrar Produto',
      description: 'Adicionar novo produto',
      icon: Package,
      href: '/dashboard/products/new',
      color: 'bg-purple-500',
      urgent: false
    },
    {
      title: 'Repor Estoque',
      description: '8 produtos em falta',
      icon: AlertTriangle,
      href: '/dashboard/inventory',
      color: 'bg-red-500',
      urgent: true
    }
  ];

  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return 'Bom dia';
    if (hour < 18) return 'Boa tarde';
    return 'Boa noite';
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatTime = (time) => {
    return new Intl.DateTimeFormat('pt-BR', {
      hour: '2-digit',
      minute: '2-digit'
    }).format(time);
  };

  return (
    <div className="space-y-6">
        {/* Header de boas-vindas */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-6 text-white">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-2xl font-bold mb-2">
                {getGreeting()}, {person?.name?.split(' ')[0]}! 👋
              </h1>
              <p className="text-blue-100 mb-4 md:mb-0">
                Hoje é {currentTime.toLocaleDateString('pt-BR', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })} • {formatTime(currentTime)}
              </p>
            </div>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="text-center">
                <p className="text-3xl font-bold">{metrics.todayTransactions}</p>
                <p className="text-blue-100 text-sm">Vendas hoje</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold">{formatCurrency(metrics.todayTotal)}</p>
                <p className="text-blue-100 text-sm">Faturamento hoje</p>
              </div>
            </div>
          </div>
        </div>        {/* Ações rápidas */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickActions.map((action, index) => {
            const Icon = action.icon;
            return (
              <Link key={index} to={action.href}>
                <Card className="relative overflow-hidden hover:shadow-lg transition-all duration-200 cursor-pointer group h-32 flex flex-col">
                  <div className="p-6 flex-1 flex items-center">
                    <div className="flex items-center w-full">
                      <div className={`p-3 rounded-lg ${action.color} group-hover:scale-110 transition-transform duration-200 flex-shrink-0`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>                      <div className="ml-4 flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors truncate">
                          {action.title}
                        </h3>
                        <p className="text-sm text-gray-600 line-clamp-2">{action.description}</p>
                      </div>
                    </div>
                    {action.urgent && (
                      <div className="absolute top-2 right-2">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Urgente
                        </span>
                      </div>
                    )}
                  </div>
                </Card>
              </Link>
            );
          })}
        </div>

        {/* Cards de métricas principais */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100">
                <DollarSign className="w-8 h-8 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Vendas Hoje</p>
                <p className="text-2xl font-semibold text-gray-900">{formatCurrency(metrics.todayTotal)}</p>
                <div className="flex items-center mt-1">
                  <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
                  <span className="text-sm text-green-600">+{todayStats.hourlyComparison.change}%</span>
                  <span className="text-sm text-gray-500 ml-1">vs ontem</span>
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100">
                <Users className="w-8 h-8 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Alunos Ativos</p>
                <p className="text-2xl font-semibold text-gray-900">{metrics.studentsCount}</p>
                <div className="flex items-center mt-1">
                  <Target className="w-4 h-4 text-blue-500 mr-1" />
                  <span className="text-sm text-blue-600">Meta: 300</span>
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-purple-100">
                <Package className="w-8 h-8 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Produtos</p>
                <p className="text-2xl font-semibold text-gray-900">{metrics.productsCount}</p>
                <div className="flex items-center mt-1">
                  <AlertTriangle className="w-4 h-4 text-red-500 mr-1" />
                  <span className="text-sm text-red-600">{metrics.lowStockCount} em falta</span>
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-orange-100">
                <BarChart3 className="w-8 h-8 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Ticket Médio</p>
                <p className="text-2xl font-semibold text-gray-900">{formatCurrency(metrics.averageTicket)}</p>
                <div className="flex items-center mt-1">
                  <Activity className="w-4 h-4 text-orange-500 mr-1" />
                  <span className="text-sm text-orange-600">{metrics.todayTransactions} vendas</span>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Gráfico de vendas e vendas recentes */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Vendas do dia */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Vendas de Hoje</h3>
              <Link to="/dashboard/reports">
                <Button variant="outline" size="sm">
                  <Eye className="w-4 h-4 mr-2" />
                  Ver Relatório
                </Button>
              </Link>
            </div>
            
            <div className="space-y-4">
              {todayStats.sales.map((sale, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <Clock className="w-5 h-5 text-gray-400 mr-3" />
                    <div>
                      <p className="font-medium text-gray-900">{sale.time}</p>
                      <p className="text-sm text-gray-600">{sale.transactions} vendas</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">{formatCurrency(sale.amount)}</p>
                    <div className="flex items-center mt-1">
                      {sale.amount > 300 ? (
                        <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
                      ) : (
                        <TrendingDown className="w-4 h-4 text-red-500 mr-1" />
                      )}
                      <span className={`text-sm ${sale.amount > 300 ? 'text-green-600' : 'text-red-600'}`}>
                        {sale.amount > 300 ? 'Alta' : 'Baixa'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 pt-4 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Total do dia</span>
                <span className="text-lg font-semibold text-gray-900">{formatCurrency(metrics.todayTotal)}</span>
              </div>
            </div>
          </Card>

          {/* Vendas recentes */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Vendas Recentes</h3>
              <Link to="/dashboard/sales">
                <Button variant="outline" size="sm">
                  <ArrowRight className="w-4 h-4 mr-2" />
                  Ver Todas
                </Button>
              </Link>
            </div>
            
            <div className="space-y-4">
              {recentSales.map((sale) => (
                <div key={sale.id} className="flex items-center justify-between p-3 border border-gray-100 rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{sale.student}</p>
                    <p className="text-sm text-gray-600">{sale.class} • {sale.items}</p>
                    <div className="flex items-center mt-1">
                      <Clock className="w-3 h-3 text-gray-400 mr-1" />
                      <span className="text-xs text-gray-500">{sale.time}</span>
                      <span className="mx-2 text-gray-300">•</span>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        sale.payment === 'PIX' ? 'bg-green-100 text-green-800' :
                        sale.payment === 'Cartão' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {sale.payment}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">{formatCurrency(sale.amount)}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Produtos com estoque baixo e resumo mensal */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Estoque baixo */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Estoque Baixo</h3>
              <Link to="/dashboard/inventory">
                <Button variant="outline" size="sm">
                  <Package className="w-4 h-4 mr-2" />
                  Gerenciar
                </Button>
              </Link>
            </div>
            
            <div className="space-y-4">
              {lowStockProducts.map((product, index) => (
                <div key={index} className="flex items-center justify-between p-3 border border-gray-100 rounded-lg">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{product.name}</p>
                    <div className="flex items-center mt-1">
                      <div className={`w-3 h-3 rounded-full mr-2 ${
                        product.status === 'critical' ? 'bg-red-500' : 'bg-yellow-500'
                      }`}></div>
                      <span className="text-sm text-gray-600">
                        {product.current} unidades restantes
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      product.status === 'critical' 
                        ? 'bg-red-100 text-red-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {product.status === 'critical' ? 'Crítico' : 'Baixo'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Resumo mensal */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Resumo do Mês</h3>
              <div className="flex items-center text-sm text-gray-600">
                <Calendar className="w-4 h-4 mr-2" />
                {currentTime.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}
              </div>
            </div>
            
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Faturamento Total</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(metrics.monthTotal)}</p>
                </div>
                <div className="p-3 bg-green-100 rounded-lg">
                  <TrendingUp className="w-8 h-8 text-green-600" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">892</p>
                  <p className="text-sm text-blue-800">Vendas realizadas</p>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <p className="text-2xl font-bold text-purple-600">{metrics.topProduct}</p>
                  <p className="text-sm text-purple-800">Produto mais vendido</p>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Meta mensal</span>
                  <span className="text-sm font-medium text-gray-900">75%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-blue-600 h-2 rounded-full" style={{ width: '75%' }}></div>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Faltam {formatCurrency(8000)} para atingir a meta
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Banner promocional */}
        <Card className="p-6 bg-gradient-to-r from-purple-500 to-pink-500 text-white">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="text-xl font-bold mb-2">📱 Novidade: App Mobile para Pais!</h3>
              <p className="text-purple-100 mb-4 md:mb-0">
                Permita que os pais acompanhem os gastos dos filhos em tempo real e façam recargas pelo celular.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button variant="outline" className="bg-white text-purple-600 border-white hover:bg-purple-50">
                <Smartphone className="w-4 h-4 mr-2" />
                Ativar App
              </Button>
              <Button variant="ghost" className="text-white border-white hover:bg-white/10">
                Saiba Mais
              </Button>
            </div>
          </div>
        </Card>
    </div>
  );
};

export default DashboardPage;